import React, { useState, useEffect } from "react";
import {
  Overlay,
  Dialog,
  Header,
  Title,
  Desc,
  Form,
  Field,
  Label,
  Input,
  Actions,
  Button,
  ApiError,
  Select,
} from "./styles";

import { storageAPI } from "api";
import { useTaskStore } from "store/task";

interface ProjectResizeProps {
  isOpen: boolean;
  onClose: () => void;
  defaultProjectName?: string;
}

const ProjectResize = ({
  isOpen,
  onClose,
  defaultProjectName,
}: ProjectResizeProps) => {
  const { addTask } = useTaskStore();

  const [projectName, setProjectName] = useState(defaultProjectName || "");
  const [volumeSize, setVolumeSize] = useState("");
  const [sizeUnit, setSizeUnit] = useState<"GB" | "TB">("TB");

  const [apiErr, setApiErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const nameInvalid = projectName.trim().length === 0;
  const sizeInvalid =
    volumeSize.trim().length === 0 ||
    isNaN(Number(volumeSize)) ||
    Number(volumeSize) <= 0;

  const canSubmit = !nameInvalid && !sizeInvalid && !loading;

  const Close = () => {
    setApiErr(null);
    setLoading(false);
    setVolumeSize("");
    setSizeUnit("TB");
    setProjectName(defaultProjectName || "");
    onClose();
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;

    try {
      setLoading(true);
      setApiErr(null);

      const res = await storageAPI.resizeProject(
        projectName.trim(),
        volumeSize.trim(),
        sizeUnit
      );

      if (res?.data?.task_id) {
        addTask({
          name: projectName,
          id: res.data.task_id,
          job: "Resize Project",
          state: "pending",
        });
      }

      Close();
    } catch (err: any) {
      setApiErr(err?.message || "Failed to resize project.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isOpen) {
      setApiErr(null);
      setLoading(false);
      setVolumeSize("");
      setSizeUnit("TB");
      setProjectName(defaultProjectName || "");
    }
  }, [isOpen, defaultProjectName]);

  if (!isOpen) return null;

  return (
    <Overlay
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) {
          e.preventDefault();
          e.stopPropagation();
          Close();
        }
      }}
    >
      <Dialog role="dialog" tabIndex={-1}>
        <Header>
          <Title>Resize Project</Title>
        </Header>

        <Desc>프로젝트의 전체 볼륨 크기를 변경합니다.</Desc>

        {apiErr && <ApiError role="alert">{apiErr}</ApiError>}

        <Form onSubmit={onSubmit}>
          <Field>
            <Label $invalid={nameInvalid}>Project Name</Label>
            <Input
              value={projectName}
              onChange={(e) => setProjectName(e.target.value)}
              placeholder="ex) cmp-production"
              $invalid={nameInvalid}
            />
          </Field>

          <Field>
            <Label $invalid={sizeInvalid}>Volume Size (TB base)</Label>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 100px",
                gap: 8,
              }}
            >
              <Input
                value={volumeSize}
                onChange={(e) => setVolumeSize(e.target.value)}
                placeholder="ex) 10"
                $invalid={sizeInvalid}
              />
              <Select
                value={sizeUnit}
                onChange={(e) => setSizeUnit(e.target.value as "GB" | "TB")}
              >
                <option value="GB">GB</option>
                <option value="TB">TB</option>
              </Select>
            </div>
          </Field>

          <Actions>
            <Button type="button" onClick={Close} data-variant="ghost">
              Cancel
            </Button>
            <Button type="submit" disabled={!canSubmit}>
              {loading ? "Resizing..." : "Resize"}
            </Button>
          </Actions>
        </Form>
      </Dialog>
    </Overlay>
  );
};

export default ProjectResize;
